/**
 * A street backdrop for a fixed map view, from any XYZ tile service.
 *
 * <p>No mapping library. The report's map does not pan or zoom - it is one square around one
 * address - and for a fixed view a tile layer is arithmetic: work out which tiles cover the
 * square, and place each one at its own offset. Leaflet and MapLibre earn their size when a map
 * moves; here they would be forty kilobytes to draw a still picture.
 *
 * <p>Everything is in Web Mercator (EPSG:3857), the projection every XYZ service publishes in, so
 * the tiles and the marks drawn over them are placed by the same transform and cannot drift
 * apart.
 *
 * <p>The provider is configuration, not a decision baked into the code: {@code VITE_MAP_TILES} is
 * the URL template and {@code VITE_MAP_ATTRIBUTION} is the credit that must appear with it. With
 * no template set there is no backdrop and no third-party request, which is the default.
 */

/** One tile's side, in pixels. Every XYZ service in use publishes 256. */
export const TILE_SIZE = 256;

/** Metres per pixel at zoom 0 on the equator; halves with every zoom level. */
const EQUATOR_METRES_PER_PIXEL = 156_543.033_928;

export interface TileLayer {
  /** The URL template, e.g. {@code https://tile.openstreetmap.org/{z}/{x}/{y}.png}. */
  template: string;
  /** The credit the provider requires, shown with the map. Never optional. */
  attribution: string;
  /** Where the credit links, when the provider gives a link. */
  attributionHref: string | null;
}

/**
 * The configured backdrop, or null when none is set.
 *
 * <p>A template without an attribution returns null rather than a map with no credit: every tile
 * service requires one, and quietly dropping it would be using somebody's work without saying so.
 */
export function configuredTileLayer(): TileLayer | null {
  const template = String(import.meta.env.VITE_MAP_TILES ?? '').trim();
  const attribution = String(import.meta.env.VITE_MAP_ATTRIBUTION ?? '').trim();
  if (!template || !attribution) {
    return null;
  }
  if (!/^https:\/\//i.test(template) || !template.includes('{z}')) {
    return null;
  }
  const href = String(import.meta.env.VITE_MAP_ATTRIBUTION_HREF ?? '').trim();
  return { template, attribution, attributionHref: href || null };
}

/** Where a longitude falls in world pixels at this zoom. */
export function worldX(longitude: number, zoom: number): number {
  return ((longitude + 180) / 360) * TILE_SIZE * 2 ** zoom;
}

/** Where a latitude falls in world pixels at this zoom. Mercator, so it is not linear in degrees. */
export function worldY(latitude: number, zoom: number): number {
  const radians = (Math.max(-85.05, Math.min(85.05, latitude)) * Math.PI) / 180;
  const merc = Math.log(Math.tan(Math.PI / 4 + radians / 2));
  return (0.5 - merc / (2 * Math.PI)) * TILE_SIZE * 2 ** zoom;
}

/** Metres on the ground per pixel at this latitude and zoom. */
export function groundResolution(latitude: number, zoom: number): number {
  return (EQUATOR_METRES_PER_PIXEL * Math.cos((latitude * Math.PI) / 180)) / 2 ** zoom;
}

/**
 * The zoom at which a square of {@code halfMetres} around the centre is about {@code targetPx}
 * across, capped at the deepest zoom most services publish.
 */
export function zoomFor(latitude: number, halfMetres: number, targetPx: number): number {
  const wanted = Math.log2((EQUATOR_METRES_PER_PIXEL * Math.cos((latitude * Math.PI) / 180) * targetPx)
    / (2 * halfMetres));
  return Math.max(1, Math.min(18, Math.round(wanted)));
}

export interface Tile {
  key: string;
  url: string;
  /** Offset from the left of the view, in the same pixels the marks are placed in. */
  left: number;
  top: number;
}

/**
 * Every tile the view covers, each with its offset inside it.
 *
 * @param left  world-pixel x of the view's left edge
 * @param top   world-pixel y of the view's top edge
 * @param size  the view's side in pixels
 */
export function tilesFor(layer: TileLayer, zoom: number, left: number, top: number, size: number): Tile[] {
  const count = 2 ** zoom;
  const firstX = Math.floor(left / TILE_SIZE);
  const firstY = Math.floor(top / TILE_SIZE);
  const lastX = Math.floor((left + size) / TILE_SIZE);
  const lastY = Math.floor((top + size) / TILE_SIZE);

  const tiles: Tile[] = [];
  for (let ty = firstY; ty <= lastY; ty += 1) {
    if (ty < 0 || ty >= count) {
      // Above the north edge or below the south edge of the world: there is no tile to ask for.
      continue;
    }
    for (let tx = firstX; tx <= lastX; tx += 1) {
      // Wrapping east-west, so a view across the antimeridian still asks for real tiles.
      const wrapped = ((tx % count) + count) % count;
      tiles.push({
        key: `${zoom}/${wrapped}/${ty}/${tx}`,
        url: layer.template
          .replace('{z}', String(zoom))
          .replace('{x}', String(wrapped))
          .replace('{y}', String(ty)),
        left: tx * TILE_SIZE - left,
        top: ty * TILE_SIZE - top,
      });
    }
  }
  return tiles;
}
